import { cn } from '@/utils/utils';
import { PopoverContentProps } from '@radix-ui/react-popover';
import { CheckIcon, ChevronDown, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../Button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../Command';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';

interface ItemCombobox {
    label: string,
    value: string,
    labelSearch?: string,
    code?: string | number | boolean
}

interface ComboboxFilterProps {
    value: string,
    onChange: (item: any) => void,
    items: ItemCombobox[],
    placeholder?: string,
    floatingLabel?: boolean,
    label?: string,
    required?: boolean,
    className?: string,
    side?: PopoverContentProps['side'],
    disabled?: boolean,
    search?: boolean,
    disableClearIcon?: boolean,
    avoidCollisions?: boolean,
    sideOffset?: number,
    arrowDownClass?: string,
    useChevronDownIcon?: boolean,
    widthFull?: boolean
}

export default function ComboboxFilter({
    value,
    onChange,
    items,
    placeholder = 'placeholder.choose',
    className,
    side = 'bottom',
    disabled = false,
    search = true,
    disableClearIcon = false,
    avoidCollisions = false,
    sideOffset = 4,
    arrowDownClass,
    useChevronDownIcon = true,
    widthFull = false
}: ComboboxFilterProps) {
    // hook
    const { t } = useTranslation();
    const [open, setOpen] = useState<boolean>(false);
    const selectValue = items?.find(item => item.value === value.toString());

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger disabled={disabled} asChild>
                <Button
                    variant={selectValue ? 'filterActive' : 'filterDefault'}
                    size='filter'
                    role='combobox'
                    aria-expanded={open}
                    className={cn(
                        'w-full flex items-center justify-between gap-[5px]',
                        className
                    )}
                >
                    <ChevronDown
                        strokeWidth={1}
                        className={cn(
                            'size-4',
                            arrowDownClass
                        )}
                    />

                    <div className={cn(
                        'flex-1 text-left',
                        selectValue && 'pr-3'
                    )}
                    >
                        {value ? selectValue?.label : `${t(placeholder)}`}
                    </div>
                    {selectValue && (
                        <X
                            strokeWidth={1}
                            className='size-4 '
                            onClick={(e) => {
                                e.stopPropagation();
                                if (!disabled) {
                                    onChange(undefined);
                                }
                            }}
                        />
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent
                onOpenAutoFocus={(e) => {
                    if (!search) {
                        e.preventDefault();
                    }
                }}
                onWheel={(e) => {
                    e.stopPropagation();
                }}
                onTouchMove={(e) => {
                    e.stopPropagation();
                }}
                widthFull={widthFull}
                className='p-0 z-[1100] max-w-[200px] max-h-[300px]'
                side={side}
                avoidCollisions={avoidCollisions}
                sideOffset={sideOffset}
                align='start'
            >
                <Command
                    filter={(value, search) => {
                        const valueStr = value ? value?.toString()?.toLocaleLowerCase() : '';
                        const searchStr = search ? search?.toString()?.toLocaleLowerCase() : '';
                        return valueStr.includes(searchStr) ? 1 : 0;
                    }}
                >
                    {search && (
                        <CommandInput placeholder={t('button.search')} />
                    )}
                    <CommandList>
                        <CommandEmpty>{t('sentences.no_data')}</CommandEmpty>
                        <CommandGroup>
                            {items?.map(item => (
                                <CommandItem
                                    key={item.value}
                                    // value={item.value}
                                    value={item?.labelSearch || item.label}
                                    onSelect={() => {
                                        value.toString() !== item.value && onChange(item);
                                        setOpen(false);
                                    }}
                                    className={cn(value && value?.toString() === item.value ? 'bg-secondary' : 'cursor-pointer', 'xsm:px-[inherit] px-[2px]')}
                                >
                                    <CheckIcon
                                        className={cn(
                                            'xsm:mr-2 mr-1 point-375:size-4 size-3',
                                            value.toString() === item.value ? 'opacity-100' : 'opacity-0'
                                        )}
                                    />
                                    <div className='truncate'>{item.label}</div>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
